import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import toastManager from "../../components/ui/toast/ToasterManager";
import Modal from "../../components/ui/modal/Modal";
import {
  useGetLoanRequests,
  useApproveLoanRequest,
  useRejectLoanRequest,
} from "../../redux/actions/loanRequestAction";
import { SelectSociety } from "./LoanSettings";

const STATUS_BADGE = {
  awaiting_fee_payment: "bg-secondary",
  submitted: "bg-warning",
  approved: "bg-success",
  rejected: "bg-danger",
};

const STATUS_LABEL = {
  awaiting_fee_payment: "Awaiting Fee Payment",
  submitted: "Pending Review",
  approved: "Approved",
  rejected: "Rejected",
};

const LoanRequests = () => {
  const { roles, user } = useSelector((state) => state.auth);
  const isSuperAdmin = roles?.includes("SuperAdmin");

  const getLoanRequests = useGetLoanRequests();
  const approveLoanRequest = useApproveLoanRequest();
  const rejectLoanRequest = useRejectLoanRequest();

  const [groupId, setGroupId] = useState(isSuperAdmin ? undefined : user?.Group?.id);
  const [chosenSociety, setChosenSociety] = useState(isSuperAdmin ? undefined : user?.Group);
  const [isSocietyPickerOpen, setIsSocietyPickerOpen] = useState(isSuperAdmin);

  const [loanRequests, setLoanRequests] = useState([]);
  const [loadingInit, setLoadingInit] = useState(false);
  const [actioningId, setActioningId] = useState(null);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const fetchLoanRequests = async () => {
    if (!groupId) return;
    setLoadingInit(true);
    try {
      const response = await getLoanRequests({ groupId });
      if (response?.payload?.status === "success") {
        setLoanRequests(response.payload.data || []);
      } else {
        toastManager.addToast({
          message: response?.payload?.message || "Failed to load loan requests",
          type: "error",
        });
      }
    } catch (error) {
      toastManager.addToast({ message: "Failed to load loan requests", type: "error" });
    } finally {
      setLoadingInit(false);
    }
  };

  useEffect(() => {
    fetchLoanRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const handleApprove = async (loanRequest) => {
    setActioningId(loanRequest.id);
    try {
      const response = await approveLoanRequest(loanRequest.id);
      if (response?.payload?.status === "success") {
        toastManager.addToast({ message: "Loan approved and disbursed", type: "success" });
        fetchLoanRequests();
      } else {
        toastManager.addToast({
          message: response?.payload?.message || "Failed to approve loan request",
          type: "error",
        });
      }
    } catch (error) {
      toastManager.addToast({ message: "Failed to approve loan request", type: "error" });
    } finally {
      setActioningId(null);
    }
  };

  const openRejectModal = (loanRequest) => {
    setRejectTarget(loanRequest);
    setRejectionReason("");
    setIsRejectModalOpen(true);
  };

  const submitRejection = async () => {
    if (!rejectTarget || !rejectionReason.trim()) return;

    setActioningId(rejectTarget.id);
    try {
      const response = await rejectLoanRequest({ id: rejectTarget.id, reason: rejectionReason });
      if (response?.payload?.status === "success") {
        toastManager.addToast({ message: "Loan request rejected", type: "success" });
        setIsRejectModalOpen(false);
        fetchLoanRequests();
      } else {
        toastManager.addToast({
          message: response?.payload?.message || "Failed to reject loan request",
          type: "error",
        });
      }
    } catch (error) {
      toastManager.addToast({ message: "Failed to reject loan request", type: "error" });
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#003399]">Loan Requests</h1>
          <p className="text-gray-600 text-sm mt-1">
            Review member loan applications, approve to disburse funds, or reject with a reason.
          </p>
          {isSuperAdmin && (
            <p className="mt-2">
              Current Society:{" "}
              <span className="font-semibold text-[#003399] bg-[#6699FF]/20 px-2 py-1 rounded">
                {chosenSociety?.name || "None selected"}
              </span>
            </p>
          )}
        </div>
        {isSuperAdmin && (
          <button onClick={() => setIsSocietyPickerOpen(true)} className="btn btn-outline-secondary">
            Change Society
          </button>
        )}
      </div>

      <div className="bg-white border border-[#003399]/20 rounded-xl shadow-sm overflow-hidden">
        {!groupId ? (
          <div className="p-8 text-center text-gray-500">Select a society to view its loan requests.</div>
        ) : loadingInit ? (
          <div className="p-8 text-center text-gray-500">Loading loan requests...</div>
        ) : loanRequests.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No loan requests yet.</div>
        ) : (
          <div className="table-responsive-custom p-4">
            <table className="table table-custom text-start table-hover w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3">Member</th>
                  <th className="p-3">Loan Type</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loanRequests.map((loanRequest) => (
                  <tr key={loanRequest.id} className="border-b">
                    <td className="p-3 font-medium text-[#003399]">
                      {loanRequest.user
                        ? `${loanRequest.user.firstName} ${loanRequest.user.lastName}`
                        : "—"}
                    </td>
                    <td className="p-3">{loanRequest.loanType?.name || "—"}</td>
                    <td className="p-3">{Number(loanRequest.amountRequested).toLocaleString()}</td>
                    <td className="p-3">
                      <span className={`badge ${STATUS_BADGE[loanRequest.status] || "bg-secondary"}`}>
                        {STATUS_LABEL[loanRequest.status] || loanRequest.status}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      {loanRequest.status === "submitted" ? (
                        actioningId === loanRequest.id ? (
                          <ClipLoader color="#003399" size={18} />
                        ) : (
                          <>
                            <button
                              onClick={() => handleApprove(loanRequest)}
                              className="btn btn-sm btn-outline-success"
                            >
                              Approve
                            </button>{" "}
                            <button
                              onClick={() => openRejectModal(loanRequest)}
                              className="btn btn-sm btn-outline-danger"
                            >
                              Reject
                            </button>
                          </>
                        )
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={isRejectModalOpen} onClose={() => setIsRejectModalOpen(false)}>
        <div className="modal__withdraw1">
          <div className="border-b pb-3 mb-4">
            <h3 className="text-xl font-bold text-[#003399]">Reject Loan Request</h3>
          </div>
          <div className="form-group mb-3">
            <label className="form-label">Reason for rejection</label>
            <textarea
              className="form-control"
              rows="4"
              placeholder="e.g. Insufficient thrift contribution history"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
            <button
              className="btn btn-secondary"
              onClick={() => setIsRejectModalOpen(false)}
              disabled={actioningId === rejectTarget?.id}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={submitRejection}
              disabled={!rejectionReason.trim() || actioningId === rejectTarget?.id}
            >
              {actioningId === rejectTarget?.id ? <ClipLoader color="#fff" size={20} /> : "Confirm Rejection"}
            </button>
          </div>
        </div>
      </Modal>

      {isSuperAdmin && (
        <Modal
          isOpen={isSocietyPickerOpen}
          onClose={() => setIsSocietyPickerOpen(false)}
          children={
            <SelectSociety
              setGroupId={setGroupId}
              setIsOpen={setIsSocietyPickerOpen}
              setChosenSociety={setChosenSociety}
            />
          }
        />
      )}
    </div>
  );
};

export default LoanRequests;
